// components/profile/FriendList.jsx
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './FriendList.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const FriendList = () => {
  const { isLoggedIn, user } = useAuth();
  const [activeTab, setActiveTab] = useState('friends');
  
  // Fetch all friends
  const { 
    data: friends = [], 
    isLoading: friendsLoading,
    refetch: refetchFriends
  } = useQuery({
    queryKey: ['friends'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data.data || [];
    },
    enabled: isLoggedIn
  });
  
  // Fetch pending requests
  const { 
    data: pendingRequests = [], 
    isLoading: pendingLoading,
    refetch: refetchPending
  } = useQuery({
    queryKey: ['friendRequests', 'pending'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/friends/pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data.data || [];
    },
    enabled: isLoggedIn && activeTab === 'pending'
  });
  
  // Fetch sent requests
  const { 
    data: sentRequests = [], 
    isLoading: sentLoading,
    refetch: refetchSent
  } = useQuery({
    queryKey: ['friendRequests', 'sent'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/api/friends/sent`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data.data || [];
    },
    enabled: isLoggedIn && activeTab === 'sent'
  });
  
  // Accept a friend request
  const acceptFriendRequest = async (requestId) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/friends/${requestId}`, 
        { status: 'accepted' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      // Refetch data
      refetchPending();
      refetchFriends();
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };
  
  // Reject a friend request
  const rejectFriendRequest = async (requestId) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/friends/${requestId}`, 
        { status: 'rejected' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      // Refetch data
      refetchPending();
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };
  
  // Cancel a sent request
  const cancelFriendRequest = async (requestId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/friends/${requestId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refetch data
      refetchSent();
    } catch (error) {
      console.error('Error canceling friend request:', error);
    }
  };
  
  // Remove a friend
  const removeFriend = async (friendshipId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/friends/${friendshipId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refetch data
      refetchFriends();
    } catch (error) {
      console.error('Error removing friend:', error);
    }
  };
  
  const renderContent = () => {
    if (activeTab === 'friends') {
      if (friendsLoading) {
        return <div className="friends-loading">Loading your friends...</div>;
      }
      
      if (friends.length === 0) {
        return (
          <div className="friends-empty">
            <div className="empty-icon">👥</div>
            <h3>No Friends Yet</h3>
            <p>Add friends to see their anime and manga collections!</p>
          </div>
        );
      }
      
      return (
        <div className="friends-list">
          {friends.map(friend => (
            <div key={friend.friendshipId} className="friend-item">
              <Link to={`/users/${friend.user.id}`} className="friend-details">
                <div className="friend-avatar">
                  <img src={friend.user.profile_picture} alt={friend.user.username} />
                </div>
                <div className="friend-info">
                  <h3 className="friend-name" title={friend.user.username}>{friend.user.username}</h3>
                  <span className="friend-since" title={`Friends since ${new Date(friend.since).toLocaleDateString()}`}>
                    Friends since {new Date(friend.since).toLocaleDateString()}
                  </span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      );
    }
    
    if (activeTab === 'pending') {
      if (pendingLoading) {
        return <div className="friends-loading">Loading friend requests...</div>;
      }
      
      if (pendingRequests.length === 0) {
        return (
          <div className="friends-empty">
            <div className="empty-icon">📭</div>
            <h3>No Pending Requests</h3>
            <p>You don't have any friend requests to respond to.</p>
          </div>
        );
      }
      
      return (
        <div className="friends-list">
          {pendingRequests.map(request => (
            <div key={request._id} className="friend-item">
              <Link to={`/users/${request.requester._id}`} className="friend-details">
                <div className="friend-avatar">
                  <img src={request.requester.profilePicture} alt={request.requester.username} />
                </div>
                <div className="friend-info">
                  <h3 className="friend-name">{request.requester.username}</h3>
                  <span className="friend-since">
                    Sent {new Date(request.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </Link>
              <div className="friend-actions">
                <button 
                  className="friend-action accept-request"
                  onClick={() => acceptFriendRequest(request._id)}
                >
                  Accept
                </button>
                <button 
                  className="friend-action reject-request"
                  onClick={() => rejectFriendRequest(request._id)}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (activeTab === 'sent') {
      if (sentLoading) {
        return <div className="friends-loading">Loading sent requests...</div>;
      }
      
      if (sentRequests.length === 0) {
        return (
          <div className="friends-empty">
            <div className="empty-icon">✉️</div>
            <h3>No Sent Requests</h3>
            <p>You haven't sent any friend requests that are pending.</p>
          </div>
        );
      }
      
      return (
        <div className="friends-list">
          {sentRequests.map(request => (
            <div key={request._id} className="friend-item">
              <Link to={`/users/${request.recipient._id}`} className="friend-details">
                <div className="friend-avatar">
                  <img src={request.recipient.profilePicture} alt={request.recipient.username} />
                </div>
                <div className="friend-info">
                  <h3 className="friend-name">{request.recipient.username}</h3>
                  <span className="friend-since">
                    Sent {new Date(request.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </Link>
              <button 
                className="friend-action cancel-request"
                onClick={() => cancelFriendRequest(request._id)}
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      );
    }
  };
  
  return (
    <div className="friends-container">
      <div className="friends-header">
        <h2>Friends</h2>
        <div className="friends-tabs">
          <button
            className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            <span>Friends</span>
            <span className="friends-count">{friends.length}</span>
          </button>
          <button
            className={`friends-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <span>Requests</span>
            <span className="friends-count">{pendingRequests.length}</span>
          </button>
          <button
            className={`friends-tab ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            <span>Sent</span>
            <span className="friends-count">{sentRequests.length}</span>
          </button>
        </div>
      </div>
      
      {renderContent()}
    </div>
  );
};

export default FriendList;